import { api } from "./baseApi";
import { ApiResponse } from "@/types";
import { getRequestLanguage } from "@/utils/requestLanguage";

export interface SystemSettings {
  id: string;
  uniqueCode: string;
  storeName: string;
  storeAddress: string;
  storePhone: string;
  storeEmail: string;
  currency: string;
  timezone: string;
  language: string;
  taxRate: number;
  receiptFooter: string;
  logoUrl?: string;
  enableWhatsApp: boolean;
  whatsAppNumber?: string;
  enableLoyalty: boolean;
  loyaltyPointsPerRupiah: number;
  loyaltyPointsValue: number;
  loyaltyMinRedeemPoints: number;
  loyaltyExpiryMonths: number;
  loyaltyTierSilverPts: number;
  loyaltyTierGoldPts: number;
  loyaltyTierPlatinumPts: number;
  loyaltyTierSilverMultiplier: number;
  loyaltyTierGoldMultiplier: number;
  loyaltyTierPlatinumMultiplier: number;
  minStockThreshold: number;
  autoBackup: boolean;
  backupFrequency: "daily" | "weekly" | "monthly";
  // Outlet-specific settings
  receiptPrinterType?: string;
  printReceiptByDefault?: boolean;
  receiptShowLogo?: boolean;
  // Customer display settings
  enableCustomerDisplay?: boolean;
  customerDisplayIdleMode?: 'logo' | 'clock' | 'video' | 'image';
  customerDisplayIdleVideoUrl?: string;
  customerDisplayIdleImageUrl?: string;
  promotionStackingEnabled?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateSettingsData {
  uniqueCode?: string;
  storeName?: string;
  storeAddress?: string;
  storePhone?: string;
  storeEmail?: string;
  currency?: string;
  timezone?: string;
  language?: string;
  taxRate?: number;
  receiptFooter?: string;
  logoUrl?: string;
  enableWhatsApp?: boolean;
  whatsAppNumber?: string;
  enableLoyalty?: boolean;
  loyaltyPointsPerRupiah?: number;
  loyaltyPointsValue?: number;
  loyaltyMinRedeemPoints?: number;
  loyaltyExpiryMonths?: number;
  loyaltyTierSilverPts?: number;
  loyaltyTierGoldPts?: number;
  loyaltyTierPlatinumPts?: number;
  loyaltyTierSilverMultiplier?: number;
  loyaltyTierGoldMultiplier?: number;
  loyaltyTierPlatinumMultiplier?: number;
  minStockThreshold?: number;
  autoBackup?: boolean;
  backupFrequency?: "daily" | "weekly" | "monthly";
  // Outlet-specific settings
  receiptPrinterType?: string;
  printReceiptByDefault?: boolean;
  receiptShowLogo?: boolean;
}

export interface BackupData {
  id: string;
  filename: string;
  size: number;
  type: "manual" | "automatic";
  status: "completed" | "failed" | "in_progress";
  createdAt: string;
}

export interface SystemInfo {
  version: string;
  environment: string;
  database: {
    type: string;
    version: string;
    status: "connected" | "disconnected";
  };
  redis: {
    status: "connected" | "disconnected";
    memory: string;
  };
  storage: {
    used: number;
    total: number;
    available: number;
  };
  appUsage?: {
    uploadsTotal: number;
    uploads: Record<string, number>;
    logs: number;
    database: number;
  };
  uptime: number;
  lastBackup?: string;
}

// Multi-Outlet Settings Types
export interface OutletSettingResponse {
  key: string;
  value: any;
  defaultValue: any;
  category: string;
  description: string;
  isDefault: boolean;
  canReset: boolean;
  lastModified?: {
    by: string;
    at: string;
  };
}

export interface BulkUpdateSettingsDto {
  settings: Record<string, any>;
}

export interface UpdateOutletSettingDto {
  key: string;
  value: any;
  requiresConfirmation?: boolean;
}

export interface CopySettingsDto {
  targetOutletIds: string[];
  settingKeys?: string[];
  overwriteExisting?: boolean;
}

export interface CopySettingsResult {
  success: number;
  failed: number;
  details: Array<{
    outletId: string;
    success: boolean;
    error?: string;
  }>;
}

export interface SettingsDiff {
  key: string;
  outlet1Value: any;
  outlet2Value: any;
  isDifferent: boolean;
  outlet1IsDefault: boolean;
  outlet2IsDefault: boolean;
}

export interface SettingsComparisonResponse {
  outlet1Id: string;
  outlet2Id: string;
  differences: SettingsDiff[];
  differencesCount: number;
}

export interface SettingsTemplate {
  name: string;
  description: string;
  settings: Record<string, any>;
}

export const settingsApi = api.injectEndpoints({
  endpoints: builder => ({
    // System Settings (Store-wide)
    getSystemSettings: builder.query<ApiResponse<SystemSettings>, { storeId: string; outletId?: string }>({
      query: ({ storeId, outletId }) => ({
        url: "/settings/system",
        params: { storeId, outletId },
      }),
      providesTags: ["Settings"],
    }),

    updateSystemSettings: builder.mutation<
      ApiResponse<SystemSettings>,
      { storeId: string; outletId: string; data: UpdateSettingsData }
    >({
      query: ({ storeId, outletId, data }) => ({
        url: "/settings/system",
        method: "PUT",
        params: { storeId, outletId },
        body: data,
      }),
      invalidatesTags: ["Settings"],
    }),

    generateUniqueCode: builder.mutation<ApiResponse<{ code: string }>, { storeId: string }>({
      query: ({ storeId }) => ({
        url: `/stores/${storeId}/generate-unique-code`,
        method: "POST",
      }),
    }),

    // ===== Multi-Outlet Settings Endpoints =====

    // Get all settings for an outlet (uses /settings/system endpoint)
    getOutletSettings: builder.query<
      ApiResponse<SystemSettings>,
      { storeId: string; outletId: string }
    >({
      query: ({ storeId, outletId }) => ({
        url: "/settings/system",
        params: { storeId, outletId },
      }),
      providesTags: (_result, _error, { outletId }) => [{ type: "Settings", id: `outlet-${outletId}` }],
    }),

    // Get specific setting for an outlet
    getOutletSetting: builder.query<
      ApiResponse<OutletSettingResponse>,
      { outletId: string; key: string }
    >({
      query: ({ outletId, key }) => `/outlets/${outletId}/settings/${key}`,
      providesTags: (_result, _error, { outletId, key }) => [
        { type: "Settings", id: `outlet-${outletId}-${key}` },
      ],
    }),

    // Update specific setting for an outlet
    updateOutletSetting: builder.mutation<
      ApiResponse<OutletSettingResponse>,
      { outletId: string; key: string; data: UpdateOutletSettingDto }
    >({
      query: ({ outletId, key, data }) => ({
        url: `/outlets/${outletId}/settings/${key}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { outletId, key }) => [
        "Settings",
        { type: "Settings", id: `outlet-${outletId}` },
        { type: "Settings", id: `outlet-${outletId}-${key}` },
      ],
    }),

    // Bulk update settings for an outlet
    bulkUpdateOutletSettings: builder.mutation<
      ApiResponse<OutletSettingResponse[]>,
      { outletId: string; data: BulkUpdateSettingsDto }
    >({
      query: ({ outletId, data }) => ({
        url: `/outlets/${outletId}/settings/bulk`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { outletId }) => [
        { type: "Settings", id: `outlet-${outletId}` },
      ],
    }),

    // Reset setting to default
    resetOutletSetting: builder.mutation<
      ApiResponse<{ success: boolean; message: string }>,
      { outletId: string; key: string }
    >({
      query: ({ outletId, key }) => ({
        url: `/outlets/${outletId}/settings/${key}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { outletId, key }) => [
        { type: "Settings", id: `outlet-${outletId}` },
        { type: "Settings", id: `outlet-${outletId}-${key}` },
      ],
    }),

    // Copy settings to other outlets
    copySettingsToOutlets: builder.mutation<
      ApiResponse<CopySettingsResult>,
      { sourceOutletId: string; data: CopySettingsDto }
    >({
      query: ({ sourceOutletId, data }) => ({
        url: `/outlets/${sourceOutletId}/settings/copy`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { data }) => [
        "Settings",
        ...data.targetOutletIds.map(id => ({ type: "Settings" as const, id: `outlet-${id}` })),
      ],
    }),

    // Compare settings between two outlets
    compareOutletSettings: builder.query<
      ApiResponse<SettingsComparisonResponse>,
      { outlet1Id: string; outlet2Id: string }
    >({
      query: ({ outlet1Id, outlet2Id }) => `/outlets/${outlet1Id}/settings/compare/${outlet2Id}`,
      providesTags: ["Settings"],
    }),

    // Get available templates
    getSettingsTemplates: builder.query<ApiResponse<SettingsTemplate[]>, void>({
      query: () => "/settings/templates",
      providesTags: ["Settings"],
    }),

    // Apply template to outlet
    applySettingsTemplate: builder.mutation<
      ApiResponse<OutletSettingResponse[]>,
      { outletId: string; templateName: string }
    >({
      query: ({ outletId, templateName }) => ({
        url: `/outlets/${outletId}/settings/template/${templateName}`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { outletId }) => [
        { type: "Settings", id: `outlet-${outletId}` },
      ],
    }),

    // CFD Media Upload
    uploadCfdMedia: builder.mutation<
      ApiResponse<{ url: string; filename: string }>,
      { file: File; storeId: string; outletId: string; mediaType: 'video' | 'image' }
    >({
      query: ({ file, storeId, outletId, mediaType }) => {
        const formData = new FormData();
        formData.append("media", file);
        return {
          url: "/settings/upload-cfd-media",
          method: "POST",
          params: { storeId, outletId, mediaType },
          body: formData,
        };
      },
      invalidatesTags: ["Settings"],
    }),

    // Logo Upload
    uploadLogo: builder.mutation<
      ApiResponse<{ url: string }>,
      { file: File; storeId: string; outletId?: string }
    >({
      query: ({ file, storeId, outletId }) => {
        const formData = new FormData();
        formData.append("logo", file);
        return {
          url: "/settings/upload-logo",
          method: "POST",
          params: { storeId, ...(outletId ? { outletId } : {}) },
          body: formData,
          // Don't set Content-Type - let browser set it with boundary
        };
      },
      invalidatesTags: ["Settings"],
    }),

    // Logo Removal
    deleteLogo: builder.mutation<ApiResponse<{ removed: string[] }>, { storeId: string }>({
      query: ({ storeId }) => ({
        url: "/settings/logo",
        method: "DELETE",
        params: { storeId },
      }),
      invalidatesTags: ["Settings"],
    }),

    // System Information
    getSystemInfo: builder.query<ApiResponse<SystemInfo>, { storeId?: string } | void>({
      query: (params) => ({
        url: "/settings/system-info",
        params: params || {},
      }),
      providesTags: ["Settings"],
    }),

    // Backup Management (Store-level, Owner-only)
    getBackups: builder.query<ApiResponse<BackupData[]>, { storeId: string }>({
      query: ({ storeId }) => ({
        url: "/settings/backups",
        params: { storeId },
      }),
      providesTags: (_result, _error, { storeId }) => [
        { type: "Settings", id: `backups-${storeId}` },
      ],
    }),

    createBackup: builder.mutation<ApiResponse<BackupData>, { storeId: string; description?: string }>({
      query: ({ storeId, ...data }) => ({
        url: "/settings/backups",
        method: "POST",
        params: { storeId },
        body: data,
      }),
      invalidatesTags: (_result, _error, { storeId }) => [
        { type: "Settings", id: `backups-${storeId}` },
      ],
    }),

    downloadBackup: builder.mutation<Blob, { storeId: string; backupId: string }>({
      query: ({ storeId, backupId }) => ({
        url: `/settings/backups/${backupId}/download`,
        method: "GET",
        params: { storeId, lang: getRequestLanguage() },
        responseHandler: async (response: Response) => response.blob(),
      }),
    }),

    deleteBackup: builder.mutation<ApiResponse<void>, { storeId: string; backupId: string }>({
      query: ({ storeId, backupId }) => ({
        url: `/settings/backups/${backupId}`,
        method: "DELETE",
        params: { storeId },
      }),
      invalidatesTags: (_result, _error, { storeId }) => [
        { type: "Settings", id: `backups-${storeId}` },
      ],
    }),

    // Restore Backup (Store-level, Owner-only)
    restoreBackup: builder.mutation<
      ApiResponse<{ message?: string }>,
      { storeId: string; backupId: string; overwrite?: boolean }
    >({
      query: ({ storeId, backupId, overwrite = false }) => ({
        url: "/settings/backup/restore",
        method: "POST",
        params: { storeId },
        body: { backupId, overwrite },
      }),
      invalidatesTags: (_result, _error, { storeId }) => [
        { type: "Settings", id: `backups-${storeId}` },
      ],
    }),

    // Database Operations
    resetDatabase: builder.mutation<ApiResponse<{ message: string }>, void>({
      query: () => ({
        url: "/settings/reset-database",
        method: "POST",
      }),
      invalidatesTags: ["Settings"],
    }),

    // Export Data (Store-level, Owner-only)
    exportData: builder.mutation<
      Blob,
      {
        storeId: string;
        tables?: string[];
        format: "sql" | "json" | "csv";
        includeMedia?: boolean;
      }
    >({
      query: ({ storeId, ...params }) => ({
        url: "/settings/export-data",
        method: "POST",
        params: { storeId, lang: getRequestLanguage() },
        body: params,
        responseHandler: async (response: Response) => response.blob(),
      }),
    }),

    // Import Data (Store-level, Owner-only)
    importData: builder.mutation<
      ApiResponse<{ message: string; imported: number }>,
      {
        storeId: string;
        file: File;
        format: "sql" | "json" | "csv" | "zip";
        overwrite?: boolean;
      }
    >({
      query: ({ storeId, file, format, overwrite }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("format", format);
        if (overwrite) formData.append("overwrite", "true");
        return {
          url: "/settings/import-data",
          method: "POST",
          params: { storeId },
          body: formData,
        };
      },
      invalidatesTags: (_result, _error, { storeId }) => [
        { type: "Settings", id: `backups-${storeId}` },
      ],
    }),

    // System Health Check
    performHealthCheck: builder.mutation<
      ApiResponse<{
        overall: "healthy" | "warning" | "critical";
        checks: {
          name: string;
          status: "pass" | "fail" | "warn";
          message: string;
        }[];
      }>,
      void
    >({
      query: () => ({
        url: "/settings/health-check",
        method: "POST",
      }),
    }),

    // Clear Cache
    clearCache: builder.mutation<
      ApiResponse<{ message: string }>,
      {
        type: "all" | "redis" | "application" | "images";
      }
    >({
      query: data => ({
        url: "/settings/clear-cache",
        method: "POST",
        body: data,
      }),
    }),
  }),
});

export const {
  // Legacy system settings
  useGetSystemSettingsQuery,
  useUpdateSystemSettingsMutation,

  // Multi-outlet settings
  useGetOutletSettingsQuery,
  useGetOutletSettingQuery,
  useUpdateOutletSettingMutation,
  useBulkUpdateOutletSettingsMutation,
  useResetOutletSettingMutation,
  useCopySettingsToOutletsMutation,
  useCompareOutletSettingsQuery,
  useGetSettingsTemplatesQuery,
  useApplySettingsTemplateMutation,

  // System operations
  useUploadCfdMediaMutation,
  useUploadLogoMutation,
  useDeleteLogoMutation,
  useGetSystemInfoQuery,
  useGetBackupsQuery,
  useCreateBackupMutation,
  useDownloadBackupMutation,
  useDeleteBackupMutation,
  useRestoreBackupMutation,
  useResetDatabaseMutation,
  useExportDataMutation,
  useImportDataMutation,
  usePerformHealthCheckMutation,
  useClearCacheMutation,
  useGenerateUniqueCodeMutation,
} = settingsApi;
